import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Award, FileDown, Loader2, AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, User, Filter } from "lucide-react";
import { toast } from "sonner";

const FILIERE_LABELS = {
    electrotechnique: "BTS Électrotechnique",
};
const NIVEAU_LABELS = {
    "1ere": "1ère année",
    "2eme": "2ème année",
};

const STATUS_META = {
    acquis: { label: "Acquis", color: "bg-emerald-500", text: "text-emerald-800", bg: "bg-emerald-50" },
    partiel: { label: "Partiel", color: "bg-amber-500", text: "text-amber-800", bg: "bg-amber-50" },
    non_acquis: { label: "Non acquis", color: "bg-red-500", text: "text-red-800", bg: "bg-red-50" },
    non_evalue: { label: "Non évalué", color: "bg-rule", text: "text-muted2", bg: "bg-paper" },
};

export default function Progression() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [referentiel, setReferentiel] = useState([]);
    const [alternants, setAlternants] = useState([]);
    const [niveauFilter, setNiveauFilter] = useState("all"); // all | 1ere | 2eme
    const [filiereFilter, setFiliereFilter] = useState("all"); // all | electrotechnique
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [showFinalBilan, setShowFinalBilan] = useState(false);
    const [editingFinalBilan, setEditingFinalBilan] = useState(null);
    const printRef = useRef(null);

    const canAuthorFinalBilan = user?.role === "responsable" || user?.role === "admin";

    const canNavigate = user?.role === "formateur" || user?.role === "admin" || user?.role === "responsable";

    useEffect(() => {
        (async () => {
            try {
                const promises = [
                    api.get(`/progression/${id}`),
                    api.get("/referentiels"),
                ];
                if (canNavigate) {
                    promises.push(api.get("/establishment/alternants").catch(() => ({ data: [] })));
                }
                const [pRes, rRes, aRes] = await Promise.all(promises);
                setData(pRes.data);
                setReferentiel(rRes.data?.electrotechnique?.competencies || []);
                if (aRes) {
                    // Sort alternants alphabetically by last name then first name for stable order
                    const sorted = [...(aRes.data || [])].sort((a, b) => {
                        const na = `${a.nom || ""} ${a.prenom || ""}`.toLowerCase();
                        const nb = `${b.nom || ""} ${b.prenom || ""}`.toLowerCase();
                        return na.localeCompare(nb);
                    });
                    setAlternants(sorted);
                }
            } catch (e) {
                console.warn("Progression: failed to load alternants/data", e);
            }
            finally { setLoading(false); }
        })();
    }, [id, canNavigate]);

    // Filter the navigable list by the responsable/formateur's picked niveau + filière.
    // When no filter is active (both "all"), every alternant of the establishment is in scope.
    const filteredAlternants = useMemo(() => {
        return alternants.filter((a) => {
            if (niveauFilter !== "all" && a.niveau !== niveauFilter) return false;
            if (filiereFilter !== "all" && a.filiere !== filiereFilter) return false;
            return true;
        });
    }, [alternants, niveauFilter, filiereFilter]);

    const currentIdx = filteredAlternants.findIndex((a) => a.id === id);
    const prevAlt = currentIdx > 0 ? filteredAlternants[currentIdx - 1] : null;
    const nextAlt = currentIdx >= 0 && currentIdx < filteredAlternants.length - 1 ? filteredAlternants[currentIdx + 1] : null;
    // `currentIdx === -1` means the currently-viewed alternant doesn't match the filter
    // (e.g. the responsable picked "2ème année" but is viewing a "1ère année" alternant).
    const currentOutOfFilter = currentIdx === -1 && alternants.length > 0;

    const downloadPdf = async () => {
        if (!printRef.current || !data) return;
        setExporting(true);
        try {
            const html2pdf = (await import("html2pdf.js")).default;
            const filename = `Progression_${data.alternant.prenom || ""}_${data.alternant.nom || ""}.pdf`.replace(/\s+/g, "_");
            await html2pdf()
                .from(printRef.current)
                .set({
                    margin: [10, 10, 10, 10],
                    filename,
                    image: { type: "jpeg", quality: 0.95 },
                    html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
                    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                })
                .save();
            toast.success("PDF téléchargé !");
        } catch {
            toast.error("Erreur lors de la génération du PDF");
        } finally { setExporting(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-16 text-center text-muted2">Chargement…</div>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="min-h-screen bg-paper">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-16 text-center">
                    <h1 className="h-display text-2xl">Progression introuvable</h1>
                    <Link to="/dashboard" className="btn-primary mt-4 inline-flex">Retour</Link>
                </div>
            </div>
        );
    }

    const total = referentiel.length;
    const acquis = referentiel.filter((c) => data.school[c.code]?.status === "acquis").length;
    const partiel = referentiel.filter((c) => data.school[c.code]?.status === "partiel").length;
    const nonAcquis = referentiel.filter((c) => data.school[c.code]?.status === "non_acquis").length;
    const seenSchool = acquis + partiel + nonAcquis;
    const seenCompany = referentiel.filter((c) => data.company_seen[c.code] > 0).length;
    // Gap : compétences vues côté école mais jamais en entreprise (et inversement)
    const gapSchoolNotCompany = referentiel.filter((c) => seenInSchool(data, c.code) && !data.company_seen[c.code]);
    const gapCompanyNotSchool = referentiel.filter((c) => data.company_seen[c.code] > 0 && !seenInSchool(data, c.code));

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="progression-page">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <Link to="/dashboard" className="text-sm font-mono text-muted2 inline-flex items-center gap-1 hover:text-ink">
                        <ArrowLeft size={14} /> Retour
                    </Link>
                    <div className="flex items-center gap-2">
                        {canNavigate && filteredAlternants.length > 1 && (
                            <div className="flex items-center gap-1 mr-1" data-testid="progression-navigator">
                                <button
                                    type="button"
                                    onClick={() => prevAlt && navigate(`/progression/${prevAlt.id}`)}
                                    disabled={!prevAlt}
                                    title={prevAlt ? `Précédent : ${prevAlt.prenom} ${prevAlt.nom}` : "Aucun alternant précédent"}
                                    className="p-2 border border-rule hover:border-orange hover:text-orange disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    data-testid="progression-prev-button"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="text-[11px] font-mono text-muted2 px-2 hidden sm:inline" data-testid="progression-position">
                                    {currentIdx >= 0 ? currentIdx + 1 : "—"}/{filteredAlternants.length}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => nextAlt && navigate(`/progression/${nextAlt.id}`)}
                                    disabled={!nextAlt}
                                    title={nextAlt ? `Suivant : ${nextAlt.prenom} ${nextAlt.nom}` : "Aucun alternant suivant"}
                                    className="p-2 border border-rule hover:border-orange hover:text-orange disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    data-testid="progression-next-button"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                        <button onClick={downloadPdf} disabled={exporting} className="btn-secondary !px-4 !py-2 text-xs" data-testid="progression-pdf-button">
                            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                            {exporting ? "Génération…" : "Télécharger en PDF"}
                        </button>
                        {canAuthorFinalBilan && (
                            <button onClick={() => { setEditingFinalBilan(null); setShowFinalBilan(true); }} className="btn-primary !px-4 !py-2 text-xs" data-testid="progression-final-bilan-button">
                                <Award size={14} /> Bilan de fin de semestre
                            </button>
                        )}
                        <Link
                            to={`/alternants/${id}/bilan-global`}
                            className="btn-outline !px-4 !py-2 text-xs inline-flex items-center gap-1.5"
                            data-testid="progression-bilan-global-button"
                        >
                            <Award size={14} /> Bilan global
                        </Link>
                    </div>
                </div>

                {/* FILTER PANEL — only for responsable/formateur/admin (navigators) */}
                {canNavigate && alternants.length > 1 && (
                    <div className="panel p-3 mb-5 bg-surface flex flex-wrap items-center gap-3" data-testid="progression-filters">
                        <div className="flex items-center gap-1.5 text-xs font-mono text-muted2">
                            <Filter size={13} />
                            <span>Filtrer la navigation :</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <label className="text-[11px] font-mono text-muted2">Niveau</label>
                            <select
                                value={niveauFilter}
                                onChange={(e) => setNiveauFilter(e.target.value)}
                                className="field-input !py-1 !text-xs"
                                data-testid="progression-filter-niveau"
                            >
                                <option value="all">Tous</option>
                                <option value="1ere">{NIVEAU_LABELS["1ere"]}</option>
                                <option value="2eme">{NIVEAU_LABELS["2eme"]}</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <label className="text-[11px] font-mono text-muted2">Filière</label>
                            <select
                                value={filiereFilter}
                                onChange={(e) => setFiliereFilter(e.target.value)}
                                className="field-input !py-1 !text-xs"
                                data-testid="progression-filter-filiere"
                            >
                                <option value="all">Toutes</option>
                                {Object.entries(FILIERE_LABELS).map(([k, label]) => (
                                    <option key={k} value={k}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="text-[11px] font-mono text-muted2 ml-auto">
                            {filteredAlternants.length}/{alternants.length} alternant{alternants.length > 1 ? "s" : ""}
                        </div>
                        {(niveauFilter !== "all" || filiereFilter !== "all") && (
                            <button
                                onClick={() => { setNiveauFilter("all"); setFiliereFilter("all"); }}
                                className="text-[11px] text-orange hover:underline"
                                data-testid="progression-filter-reset"
                            >
                                Réinitialiser
                            </button>
                        )}
                    </div>
                )}

                {/* Out-of-filter warning */}
                {currentOutOfFilter && (
                    <div className="panel p-3 mb-5 bg-amber-50 border-amber-200 flex items-center gap-2 text-xs text-amber-800" data-testid="progression-filter-out">
                        <AlertTriangle size={14} />
                        <span>
                            <strong>{data.alternant.prenom} {data.alternant.nom}</strong> ne correspond pas au filtre actuel. Réinitialisez pour naviguer à nouveau.
                        </span>
                    </div>
                )}

                <div ref={printRef}>
                    <header className="border-b-4 border-orange pb-6 mb-8">
                        <div className="label-eyebrow mb-2">Progression — BTS Électrotechnique</div>
                        <h1 className="h-display text-4xl font-semibold">{data.alternant.prenom} {data.alternant.nom}</h1>
                        <div className="text-xs font-mono text-muted2 mt-2">{data.alternant.email}{data.alternant.phone ? ` · ${data.alternant.phone}` : ""}</div>
                        {/* Niveau picker — formateur / responsable / admin can flip 1ère ↔ 2ème at any time */}
                        {canNavigate && (
                            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="progression-niveau-picker">
                                <span className="text-[11px] font-mono text-muted2">Niveau :</span>
                                {[
                                    { k: "1ere", label: "1ère année" },
                                    { k: "2eme", label: "2ème année" },
                                ].map((n) => {
                                    const active = data.alternant.niveau === n.k;
                                    return (
                                        <button
                                            key={n.k}
                                            type="button"
                                            disabled={active}
                                            onClick={async () => {
                                                try {
                                                    await api.patch(`/establishment/alternants/${id}/niveau`, { niveau: n.k });
                                                    toast.success(`${data.alternant.prenom} est maintenant en ${n.label}`);
                                                    const { data: fresh } = await api.get(`/progression/${id}`);
                                                    setData(fresh);
                                                    // Refresh the alternants list so the navigator & filters update
                                                    try {
                                                        const { data: alts } = await api.get("/establishment/alternants");
                                                        setAlternants(alts);
                                                    } catch { /* ignore */ }
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || "Erreur");
                                                }
                                            }}
                                            className={`px-2.5 py-1 text-[11px] transition-colors ${active ? "bg-orange text-white border border-orange cursor-default" : "bg-paper border border-rule hover:border-orange hover:text-orange"}`}
                                            data-testid={`progression-set-niveau-${n.k}`}
                                        >
                                            {n.label}{active ? " · actuel" : ""}
                                        </button>
                                    );
                                })}
                                {data.alternant.niveau && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (!window.confirm("Retirer le niveau de cet alternant ?")) return;
                                            try {
                                                await api.patch(`/establishment/alternants/${id}/niveau`, { niveau: null });
                                                toast.success("Niveau retiré");
                                                const { data: fresh } = await api.get(`/progression/${id}`);
                                                setData(fresh);
                                                try {
                                                    const { data: alts } = await api.get("/establishment/alternants");
                                                    setAlternants(alts);
                                                } catch { /* ignore */ }
                                            } catch (e) {
                                                toast.error(e.response?.data?.detail || "Erreur");
                                            }
                                        }}
                                        className="text-[11px] text-muted2 hover:text-destructive hover:underline"
                                        data-testid="progression-clear-niveau"
                                    >
                                        Retirer
                                    </button>
                                )}
                            </div>
                        )}
                    </header>

                    {/* STATS */}
                    <div className="grid sm:grid-cols-4 gap-px bg-rule border border-rule mb-8">
                        <Stat label="Compétences acquises" value={`${acquis}/${total}`} accent="text-emerald-700" />
                        <Stat label="Partiellement" value={`${partiel}/${total}`} accent="text-amber-700" />
                        <Stat label="Non acquises" value={`${nonAcquis}/${total}`} accent="text-red-700" />
                        <Stat label="Vues en entreprise" value={`${seenCompany}/${total}`} accent="text-orange" />
                    </div>

                    {/* Progress bar */}
                    <div className="mb-8">
                        <div className="label-eyebrow mb-2">Avancement global (école)</div>
                        <div className="h-3 w-full bg-rule rounded-sm overflow-hidden flex">
                            <div className="bg-emerald-500" style={{ width: `${(acquis / Math.max(total, 1)) * 100}%` }} />
                            <div className="bg-amber-500" style={{ width: `${(partiel / Math.max(total, 1)) * 100}%` }} />
                            <div className="bg-red-500" style={{ width: `${(nonAcquis / Math.max(total, 1)) * 100}%` }} />
                        </div>
                        <div className="text-[11px] font-mono text-muted2 mt-1">{seenSchool} compétence(s) évaluée(s) sur {total}</div>
                    </div>

                    {/* CONSOLIDATED SYNTHESIS — aggregates all formateurs */}
                    {data.consolidated && (data.per_formateur || []).length > 0 && (
                        <section className="mb-10 panel p-5 border-2 border-ink" data-testid="progression-consolidated">
                            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                                <div>
                                    <div className="label-eyebrow">Synthèse consolidée</div>
                                    <h2 className="h-display text-2xl font-semibold">
                                        {data.consolidated.progress_percent}% <span className="text-sm font-mono text-muted2">de progression globale</span>
                                    </h2>
                                </div>
                                <div className="text-xs text-muted2 font-mono">
                                    Agrégé depuis {(data.per_formateur || []).length} formateur(s) · dernière éval retenue par compétence
                                </div>
                            </div>
                            {/* Breakdown by formateur */}
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-testid="progression-per-formateur">
                                {(data.per_formateur || []).map((f) => {
                                    const totalEv = (f.acquis || 0) + (f.partiel || 0) + (f.non_acquis || 0);
                                    const pct = totalEv > 0 ? Math.round(((f.acquis + 0.5 * f.partiel) / totalEv) * 100) : 0;
                                    return (
                                        <div key={f.author_id} className="border border-rule p-3" data-testid={`progression-formateur-${f.author_id}`}>
                                            <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                                                <User size={13} className="text-orange" /> {f.author_name}
                                            </div>
                                            <div className="text-[10px] font-mono text-muted2 mt-0.5">{f.courses_count} séance(s) · {f.competencies_count} comp. évaluées</div>
                                            <div className="mt-2 flex items-center gap-0.5 h-2 w-full bg-rule overflow-hidden">
                                                <div className="bg-emerald-500 h-full" style={{ width: `${totalEv ? (f.acquis / totalEv) * 100 : 0}%` }} />
                                                <div className="bg-amber-500 h-full" style={{ width: `${totalEv ? (f.partiel / totalEv) * 100 : 0}%` }} />
                                                <div className="bg-red-500 h-full" style={{ width: `${totalEv ? (f.non_acquis / totalEv) * 100 : 0}%` }} />
                                            </div>
                                            <div className="mt-1.5 text-[11px] font-mono flex items-center justify-between">
                                                <span className="flex items-center gap-0.5 text-emerald-700">●{f.acquis}</span>
                                                <span className="flex items-center gap-0.5 text-amber-700">●{f.partiel}</span>
                                                <span className="flex items-center gap-0.5 text-red-700">●{f.non_acquis}</span>
                                                <span className="font-bold text-ink">{pct}%</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* STANDALONE TUTOR EVALUATIONS — tutor evaluations without bilan */}
                    {(data.standalone_tutor_evaluations || []).length > 0 && (
                        <section className="mb-10 panel p-5 border-2 border-orange/30" data-testid="progression-standalone-tutor-evals">
                            <div className="mb-3">
                                <div className="label-eyebrow">Évaluations tuteur libres</div>
                                <h2 className="h-display text-xl font-semibold">
                                    {data.standalone_tutor_evaluations.length} évaluation(s) déposée(s) sans bilan
                                </h2>
                                <p className="text-xs text-muted2 mt-1">Évaluations transmises directement par le tuteur d'entreprise, sans attendre que l'alternant rédige son bilan.</p>
                            </div>
                            <ul className="space-y-3">
                                {data.standalone_tutor_evaluations.map((se) => {
                                    const ev = se.tutor_evaluation || {};
                                    const tutorName = `${ev.tutor_prenom || ""} ${ev.tutor_nom || ""}`.trim();
                                    return (
                                        <li key={se.id} className="border border-rule p-3" data-testid={`standalone-tutor-eval-${se.id}`}>
                                            <div className="flex items-baseline flex-wrap gap-2">
                                                <span className="pill bg-orange text-white">Tuteur · {tutorName || "Anonyme"}</span>
                                                {ev.tutor_fonction && <span className="text-[11px] font-mono text-muted2">{ev.tutor_fonction}</span>}
                                                {typeof ev.note_globale === "number" && (
                                                    <span className={`pill ${ev.note_globale >= 15 ? "bg-emerald-100 text-emerald-800" : ev.note_globale >= 10 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"} border`}>
                                                        {ev.note_globale}/20
                                                    </span>
                                                )}
                                                <span className="text-[11px] font-mono text-muted2">Reçu le {new Date(se.created_at).toLocaleDateString("fr-FR")}</span>
                                            </div>
                                            {ev.appreciation_generale && (
                                                <p className="text-sm text-ink/80 mt-2 whitespace-pre-wrap leading-relaxed">{ev.appreciation_generale}</p>
                                            )}
                                            {(ev.points_forts || ev.points_a_ameliorer) && (
                                                <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs">
                                                    {ev.points_forts && (
                                                        <div className="p-2 bg-emerald-50 border border-emerald-200">
                                                            <div className="label-eyebrow text-[9px] text-emerald-800 mb-0.5">Points forts</div>
                                                            <p className="text-emerald-900 whitespace-pre-wrap">{ev.points_forts}</p>
                                                        </div>
                                                    )}
                                                    {ev.points_a_ameliorer && (
                                                        <div className="p-2 bg-amber-50 border border-amber-200">
                                                            <div className="label-eyebrow text-[9px] text-amber-800 mb-0.5">À améliorer</div>
                                                            <p className="text-amber-900 whitespace-pre-wrap">{ev.points_a_ameliorer}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {(user?.role === "responsable" || user?.role === "admin" || user?.role === "formateur") && (
                                                <button
                                                    onClick={async () => {
                                                        if (!window.confirm(`Supprimer définitivement cette évaluation du tuteur ${tutorName || "inconnu"} ? Cette action est irréversible.`)) return;
                                                        try {
                                                            await api.delete(`/tutor-standalone-evaluations/${se.id}`);
                                                            toast.success("Évaluation supprimée");
                                                            const { data: fresh } = await api.get(`/progression/${id}`);
                                                            setData(fresh);
                                                        } catch (e) {
                                                            toast.error(e.response?.data?.detail || "Erreur");
                                                        }
                                                    }}
                                                    className="text-[11px] text-destructive hover:underline mt-2"
                                                    data-testid={`standalone-tutor-eval-delete-${se.id}`}
                                                >
                                                    Supprimer
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    )}

                    {/* FINAL BILANS — per semester synthesis grouped by school year */}
                    {(data.final_bilans || []).length > 0 && (() => {
                        // Group by school_year, descending (most recent first)
                        const grouped = {};
                        (data.final_bilans || []).forEach((fb) => {
                            const yr = fb.school_year || "Année non renseignée";
                            if (!grouped[yr]) grouped[yr] = [];
                            grouped[yr].push(fb);
                        });
                        const sortedYears = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
                        return (
                            <section className="mb-10 panel p-5 border-2 border-orange/40" data-testid="progression-final-bilans">
                                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                                    <div>
                                        <div className="label-eyebrow">Bilans finaux · archivés par année scolaire</div>
                                        <h2 className="h-display text-xl font-semibold">{data.final_bilans.length} synthèse(s) · {sortedYears.length} année(s) scolaire(s)</h2>
                                    </div>
                                </div>
                                {sortedYears.map((yr) => (
                                    <div key={yr} className="mb-5 last:mb-0" data-testid={`final-bilans-year-${yr}`}>
                                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-orange/30">
                                            <span className="pill bg-orange text-white">Année scolaire {yr}</span>
                                            <span className="text-[11px] font-mono text-muted2">{grouped[yr].length} synthèse(s)</span>
                                        </div>
                                        <ul className="space-y-3">
                                            {grouped[yr].map((fb) => {
                                                const isAuthor = fb.author_id === user?.id;
                                                const totalRef = referentiel.length || 1;
                                                const bilanPct = Math.round(((fb.validated_codes || []).length / totalRef) * 100);
                                                return (
                                                    <li key={fb.id} className="border border-rule p-3" data-testid={`final-bilan-${fb.id}`}>
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span className="pill bg-ink text-white">{fb.semester}</span>
                                                                    {typeof fb.note === "number" && (
                                                                        <span className={`pill ${fb.note >= 15 ? "bg-emerald-100 text-emerald-800" : fb.note >= 10 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"} border`}>
                                                                            {fb.note}/20
                                                                        </span>
                                                                    )}
                                                                    <span className="pill bg-orange/10 text-orange border border-orange/30">
                                                                        {bilanPct}% validé
                                                                    </span>
                                                                    <span className="text-[11px] font-mono text-muted2">Par {fb.author_name}</span>
                                                                </div>
                                                                <div className="flex gap-3 text-[11px] font-mono text-muted2 mt-2 flex-wrap">
                                                                    <span className="text-emerald-700">✓ {(fb.validated_codes || []).length} validée(s)</span>
                                                                    <span className="text-amber-700">◑ {(fb.pending_codes || []).length} à acquérir</span>
                                                                    <span className="text-muted2">/ {totalRef} comp.</span>
                                                                    {fb.bilan_date && (
                                                                        <span className="text-muted2">· Conduit le {new Date(fb.bilan_date).toLocaleDateString("fr-FR")}</span>
                                                                    )}
                                                                </div>
                                                                {fb.comment && <p className="text-sm text-ink/80 mt-2 whitespace-pre-wrap leading-relaxed">{fb.comment}</p>}
                                                            </div>
                                                            {(isAuthor || user?.role === "responsable" || user?.role === "admin") && (
                                                                <div className="flex flex-col gap-1 shrink-0 mt-1">
                                                                    <button
                                                                        onClick={() => { setEditingFinalBilan(fb); setShowFinalBilan(true); }}
                                                                        className="text-[11px] text-orange hover:underline"
                                                                        data-testid={`final-bilan-edit-${fb.id}`}
                                                                    >
                                                                        Modifier
                                                                    </button>
                                                                    <button
                                                                        onClick={async () => {
                                                                            const author = isAuthor ? "" : ` (rédigée par ${fb.author_name})`;
                                                                            if (!window.confirm(`Supprimer définitivement la synthèse « ${fb.semester} »${author} ? Cette action est irréversible.`)) return;
                                                                            try {
                                                                                await api.delete(`/final-bilans/${fb.id}`);
                                                                                toast.success("Synthèse supprimée");
                                                                                const { data: fresh } = await api.get(`/progression/${id}`);
                                                                                setData(fresh);
                                                                            } catch (e) {
                                                                                toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
                                                                            }
                                                                        }}
                                                                        className="text-[11px] text-destructive hover:underline"
                                                                        data-testid={`final-bilan-delete-${fb.id}`}
                                                                    >
                                                                        Supprimer
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </section>
                        );
                    })()}

                    {/* FORMATEUR COMMENTS feed */}
                    {(data.formateur_comments || []).length > 0 && (
                        <section className="mb-10 panel p-5" data-testid="progression-comments">
                            <div className="label-eyebrow">Commentaires des formateurs</div>
                            <h2 className="h-display text-xl font-semibold mb-4">{data.formateur_comments.length} retour(s) sur la progression</h2>
                            <ul className="space-y-3">
                                {data.formateur_comments.map((c, i) => (
                                    <li key={`${c.date || ""}-${c.author_name || ""}-${i}`} className="border-l-2 border-orange/60 pl-4 py-1" data-testid={`progression-comment-${i}`}>
                                        <div className="text-xs font-mono text-muted2 flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-ink">{c.author_name || "Formateur"}</span>
                                            <span>·</span>
                                            <span>{c.date}</span>
                                            {c.course_title && <><span>·</span><span className="italic">{c.course_title}</span></>}
                                        </div>
                                        <p className="text-sm text-ink/80 mt-1 leading-relaxed whitespace-pre-wrap">{c.comment}</p>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* PER COMPETENCY DETAIL */}
                    <section className="mb-10">
                        <h2 className="h-display text-2xl font-semibold mb-4">Détail par compétence</h2>
                        <div className="border border-rule">
                            {referentiel.map((c) => {
                                const sch = data.school[c.code];
                                const meta = STATUS_META[sch?.status || "non_evalue"];
                                const company = data.company_seen[c.code] || 0;
                                return (
                                    <div key={c.code} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-rule last:border-b-0 items-center text-sm">
                                        <div className="col-span-1 font-mono text-xs text-orange font-bold">{c.code}</div>
                                        <div className="col-span-7 text-xs leading-snug">{c.label}</div>
                                        <div className="col-span-2">
                                            <span className={`pill ${meta.bg} ${meta.text} inline-flex`}>
                                                <span className={`w-1.5 h-1.5 ${meta.color}`} /> {meta.label}
                                            </span>
                                        </div>
                                        <div className="col-span-2 text-right text-[11px] font-mono">
                                            {company > 0 ? <span className="text-orange">Entreprise · {company}×</span> : <span className="text-muted2">Non vu en entreprise</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* GAP ANALYSIS */}
                    {(gapSchoolNotCompany.length > 0 || gapCompanyNotSchool.length > 0) && (
                        <section className="mb-10">
                            <h2 className="h-display text-2xl font-semibold mb-4 flex items-center gap-2"><AlertTriangle size={20} className="text-orange" /> Écarts école / entreprise</h2>
                            <div className="grid md:grid-cols-2 gap-3">
                                <div className="panel p-4 bg-amber-50/40 border-l-4 border-l-amber-500">
                                    <div className="label-eyebrow mb-2">Vues à l'école, jamais en entreprise</div>
                                    {gapSchoolNotCompany.length === 0 ? <p className="text-xs italic text-muted2">Aucun écart.</p> : (
                                        <ul className="space-y-1 text-xs">
                                            {gapSchoolNotCompany.map((c) => (
                                                <li key={c.code}><span className="font-mono text-orange font-bold mr-2">{c.code}</span>{c.label}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="panel p-4 bg-emerald-50/40 border-l-4 border-l-emerald-500">
                                    <div className="label-eyebrow mb-2">Vues en entreprise, pas à l'école</div>
                                    {gapCompanyNotSchool.length === 0 ? <p className="text-xs italic text-muted2">Aucun écart.</p> : (
                                        <ul className="space-y-1 text-xs">
                                            {gapCompanyNotSchool.map((c) => (
                                                <li key={c.code}><span className="font-mono text-orange font-bold mr-2">{c.code}</span>{c.label}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

                    {/* COMPANY HISTORY */}
                    <section className="mb-10">
                        <h2 className="h-display text-2xl font-semibold mb-4 flex items-center gap-2"><Award size={20} className="text-orange" /> Bilans d'alternance ({data.company_history.length})</h2>
                        {data.company_history.length === 0 ? (
                            <p className="text-sm italic text-muted2">Aucun bilan d'alternance soumis pour l'instant.</p>
                        ) : (
                            <div className="border border-rule">
                                {data.company_history.map((b) => (
                                    <div key={b.bilan_id} className="px-4 py-2.5 border-b border-rule last:border-b-0 text-sm flex justify-between items-center">
                                        <div>
                                            <div className="font-medium">{b.entreprise || "—"}</div>
                                            <div className="text-[11px] font-mono text-muted2">{b.date_debut} → {b.date_fin} · {b.codes.length} compétence(s)</div>
                                        </div>
                                        <span className={`pill ${b.tutor_done ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"} inline-flex items-center`}>
                                            {b.tutor_done ? <CheckCircle2 size={11} className="mr-1" /> : <AlertTriangle size={11} className="mr-1" />}
                                            {b.tutor_done ? "Tuteur évalué" : "En attente tuteur"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                {showFinalBilan && (
                    <FinalBilanModal
                        alternantId={id}
                        editing={editingFinalBilan}
                        referentiel={referentiel}
                        consolidated={data.consolidated}
                        school={data.school}
                        onClose={() => { setShowFinalBilan(false); setEditingFinalBilan(null); }}
                        onSaved={async () => {
                            setShowFinalBilan(false);
                            setEditingFinalBilan(null);
                            // Reload progression
                            try {
                                const { data: fresh } = await api.get(`/progression/${id}`);
                                setData(fresh);
                            } catch { /* ignore */ }
                        }}
                    />
                )}
            </main>
        </div>
    );
}

function FinalBilanModal({ alternantId, editing, referentiel, consolidated, school, onClose, onSaved }) {
    // Default school year: Sept N → Aug N+1
    const today = new Date();
    const defaultYear = today.getMonth() >= 8 // 0-indexed: 8 = September
        ? `${today.getFullYear()}-${today.getFullYear() + 1}`
        : `${today.getFullYear() - 1}-${today.getFullYear()}`;
    const defaultDate = today.toISOString().slice(0, 10);

    const [semester, setSemester] = useState(editing?.semester || `S1 ${defaultYear}`);
    const [schoolYear, setSchoolYear] = useState(editing?.school_year || defaultYear);
    const [bilanDate, setBilanDate] = useState(editing?.bilan_date || defaultDate);
    const [note, setNote] = useState(editing?.note != null ? String(editing.note) : "");
    const [comment, setComment] = useState(editing?.comment || "");

    // Auto-seed validated/pending from consolidated data (what the student has acquired across ALL formateurs)
    const seedFromConsolidated = () => {
        const v = new Set();
        const p = new Set();
        Object.entries(school || {}).forEach(([code, slot]) => {
            if (slot?.status === "acquis") v.add(code);
            else if (slot?.status === "partiel" || slot?.status === "non_acquis") p.add(code);
        });
        return { validated: v, pending: p };
    };

    const [validated, setValidated] = useState(() => {
        if (editing?.validated_codes) return new Set(editing.validated_codes);
        return seedFromConsolidated().validated;
    });
    const [pending, setPending] = useState(() => {
        if (editing?.pending_codes) return new Set(editing.pending_codes);
        return seedFromConsolidated().pending;
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const toggleValidated = (code) => {
        setValidated((s) => {
            const n = new Set(s);
            if (n.has(code)) n.delete(code); else { n.add(code); pending.delete(code); setPending(new Set(pending)); }
            return n;
        });
    };
    const togglePending = (code) => {
        setPending((s) => {
            const n = new Set(s);
            if (n.has(code)) n.delete(code); else { n.add(code); validated.delete(code); setValidated(new Set(validated)); }
            return n;
        });
    };

    // Computed note based on the CURRENT selection of validated/pending competencies
    // (not only from consolidated but from what the formateur has ticked).
    const computedNote = useMemo(() => {
        const v = validated.size;
        const p = pending.size;
        const totalRef = referentiel.length || 1;
        // "acquis" = 1, "partiel/pending" = 0.5 — classic Qualiopi weighting
        const score = v + 0.5 * p;
        return ((score / totalRef) * 20).toFixed(1);
    }, [validated, pending, referentiel]);

    // Auto-fill the note field from the computed value
    const applyComputedNote = () => {
        setNote(computedNote);
        toast.success(`Note auto-calculée : ${computedNote}/20`);
    };

    // Reset the selection to match the consolidated progression
    const resetFromConsolidated = () => {
        const { validated: v, pending: p } = seedFromConsolidated();
        setValidated(v);
        setPending(p);
        toast.success(`Auto-rempli depuis la progression : ${v.size} validée(s) · ${p.size} à acquérir`);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                alternant_id: alternantId,
                semester: semester.trim(),
                school_year: schoolYear.trim(),
                bilan_date: bilanDate || null,
                note: note ? parseFloat(note) : null,
                comment: comment.trim(),
                validated_codes: Array.from(validated),
                pending_codes: Array.from(pending),
            };
            if (editing?.id) {
                await api.put(`/final-bilans/${editing.id}`, payload);
                toast.success("Bilan de semestre mis à jour");
            } else {
                await api.post("/final-bilans", payload);
                toast.success("Bilan de semestre créé");
            }
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!editing?.id) return;
        if (!window.confirm("Supprimer définitivement ce bilan de semestre ?")) return;
        setDeleting(true);
        try {
            await api.delete(`/final-bilans/${editing.id}`);
            toast.success("Bilan supprimé");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setDeleting(false);
        }
    };

    // Totals from current selection
    const totalRefCount = referentiel.length;
    const validatedPct = totalRefCount > 0 ? Math.round((validated.size / totalRefCount) * 100) : 0;

    return (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose} data-testid="final-bilan-modal">
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-paper max-w-3xl w-full max-h-[90vh] flex flex-col border border-rule">
                <div className="p-5 border-b border-rule">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="label-eyebrow">Bilan de fin de semestre</div>
                            <h2 className="h-display text-xl font-semibold mt-1">{editing ? "Modifier le bilan" : "Nouveau bilan final"}</h2>
                            <p className="text-[11px] text-muted2 mt-1">Synthèse officielle — agrège les évaluations de <strong>tous les formateurs</strong> pour une progression globale.</p>
                        </div>
                        <button type="button" onClick={onClose} className="text-muted2 hover:text-ink"><AlertTriangle size={16} /></button>
                    </div>

                    {/* CONSOLIDATED STATS BAR — visible snapshot of current progression from all formateurs */}
                    {consolidated && (
                        <div className="mt-4 p-3 bg-ink text-white border border-ink" data-testid="final-bilan-consolidated-bar">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="label-eyebrow text-orange/80 mb-0.5">Progression agrégée (tous formateurs)</div>
                                    <div className="text-3xl font-bold font-mono leading-none">{consolidated.progress_percent || 0}%</div>
                                </div>
                                <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-[11px] font-mono">
                                    <span className="text-emerald-300">● {consolidated.acquis} acquis</span>
                                    <span className="text-amber-300">● {consolidated.partiel} partiel</span>
                                    <span className="text-red-300">● {consolidated.non_acquis} non acquis</span>
                                    <span className="text-white/50 col-span-3 mt-1">/ {consolidated.total_referentiel} compétences au référentiel</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={resetFromConsolidated}
                                    className="px-3 py-1.5 text-xs bg-orange hover:bg-orange/90 text-white"
                                    data-testid="final-bilan-refill"
                                    title="Remplir automatiquement les cases validées / à acquérir depuis la progression de tous les formateurs"
                                >
                                    ⚡ Auto-remplir
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-2 gap-3 mt-4">
                        <div>
                            <label className="label-eyebrow mb-1.5 block">Semestre *</label>
                            <input className="field-input" value={semester} onChange={(e) => setSemester(e.target.value)} placeholder="ex : S1 2025-2026" required data-testid="final-bilan-semester" />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-1.5 block flex items-center justify-between">
                                <span>Note finale /20</span>
                                <button type="button" onClick={applyComputedNote} className="text-[10px] text-orange hover:underline normal-case tracking-normal" data-testid="final-bilan-auto-note">
                                    ⚡ Auto ({computedNote})
                                </button>
                            </label>
                            <input className="field-input" type="number" min="0" max="20" step="0.5" value={note} onChange={(e) => setNote(e.target.value)} placeholder={computedNote} data-testid="final-bilan-note" />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-1.5 block">Année scolaire *</label>
                            <input className="field-input" value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)} placeholder="ex : 2025-2026" required pattern="\d{4}-\d{4}" title="Format attendu : 2025-2026" data-testid="final-bilan-school-year" />
                            <p className="text-[10px] text-muted2 mt-1 normal-case">Permet d'archiver les synthèses par année</p>
                        </div>
                        <div>
                            <label className="label-eyebrow mb-1.5 block">Date de la synthèse</label>
                            <input className="field-input" type="date" value={bilanDate} onChange={(e) => setBilanDate(e.target.value)} data-testid="final-bilan-date" />
                            <p className="text-[10px] text-muted2 mt-1 normal-case">Date à laquelle la synthèse a été conduite</p>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div>
                        <label className="label-eyebrow mb-1.5 block flex items-center justify-between">
                            <span>Compétences validées ({validated.size}) / À acquérir ({pending.size})</span>
                            <span className="text-[10px] font-mono text-orange normal-case tracking-normal">{validatedPct}% validé</span>
                        </label>
                        <p className="text-[11px] text-muted2 mb-2">Pré-rempli à partir de la progression consolidée de <strong>tous les formateurs</strong>. Ajustez si besoin — la note auto se met à jour.</p>
                        <div className="border border-rule max-h-72 overflow-y-auto">
                            {referentiel.map((c) => {
                                const isV = validated.has(c.code);
                                const isP = pending.has(c.code);
                                // Color hint matching latest consolidated status
                                const latest = school?.[c.code]?.status;
                                const hint = latest === "acquis" ? "bg-emerald-50" : latest === "partiel" ? "bg-amber-50" : latest === "non_acquis" ? "bg-red-50" : "";
                                return (
                                    <div key={c.code} className={`flex items-center gap-2 px-3 py-1.5 border-b border-rule last:border-b-0 text-xs ${hint}`}>
                                        <span className="font-mono text-orange font-bold w-10 shrink-0">{c.code}</span>
                                        <span className="flex-1 truncate">{c.label}</span>
                                        <button type="button" onClick={() => toggleValidated(c.code)} className={`px-2 py-0.5 text-[11px] border transition-colors ${isV ? "bg-emerald-500 text-white border-emerald-500" : "border-rule hover:border-emerald-500"}`} data-testid={`final-bilan-validate-${c.code}`}>✓ Validée</button>
                                        <button type="button" onClick={() => togglePending(c.code)} className={`px-2 py-0.5 text-[11px] border transition-colors ${isP ? "bg-amber-500 text-white border-amber-500" : "border-rule hover:border-amber-500"}`} data-testid={`final-bilan-pending-${c.code}`}>◑ À acquérir</button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="label-eyebrow mb-1.5 block">Commentaire général</label>
                        <textarea rows={5} className="field-input resize-none text-sm" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={4000} placeholder="Synthèse globale : points forts, axes d'amélioration, recommandations pour le semestre suivant…" data-testid="final-bilan-comment" />
                        <div className="text-[10px] font-mono text-muted2 mt-1">{comment.length}/4000</div>
                    </div>
                </div>

                <div className="p-4 border-t border-rule flex items-center justify-between gap-2 bg-paper">
                    <div>
                        {editing && (
                            <button type="button" onClick={remove} disabled={deleting} className="text-xs text-destructive hover:underline" data-testid="final-bilan-delete">
                                {deleting ? "Suppression…" : "Supprimer ce bilan"}
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="btn-outline !text-xs">Annuler</button>
                        <button type="submit" disabled={saving} className="btn-primary !text-xs" data-testid="final-bilan-save">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                            {editing ? "Mettre à jour" : "Enregistrer le bilan"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function seenInSchool(data, code) {
    const s = data.school[code];
    return s && (s.status === "acquis" || s.status === "partiel" || s.status === "non_acquis");
}

const Stat = ({ label, value, accent }) => (
    <div className="bg-paper p-5">
        <div className="label-eyebrow">{label}</div>
        <div className={`h-display text-3xl font-bold mt-1 ${accent || ""}`}>{value}</div>
    </div>
);
